// EvProfitLossDetailModal.jsx
// Fields matched exactly to getSpecificEvProfitLoss API response

import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Grid, Paper, Box, Typography, Chip, Divider, Alert, CircularProgress
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import PaymentIcon       from "@mui/icons-material/Payment";
import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon    from "@mui/icons-material/TrendingUp";
import TrendingDownIcon  from "@mui/icons-material/TrendingDown";
import WarningAmberIcon  from "@mui/icons-material/WarningAmber";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt     = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN") : "—";

const Label = ({ children, color = "#777" }) => (
  <Typography
    variant="caption"
    sx={{ color, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", fontSize: "0.62rem" }}
  >
    {children}
  </Typography>
);

const Val = ({ children, color }) => (
  <Typography variant="body2" sx={{ fontWeight: 700, color: color || "#111", mt: 0.1 }}>
    {children ?? "—"}
  </Typography>
);

const InfoBlock = ({ label, value, color }) => (
  <Box>
    <Label>{label}</Label>
    <Val color={color}>{value}</Val>
  </Box>
);

// ─── COMPONENT ───────────────────────────────────────────────────────────────
const EvProfitLossDetailModal = ({ open, onClose, detailData, detailLoading }) => {
  const d = detailData;
   return (
    <Dialog
      open={open} onClose={onClose}
      fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── HEADER ── */}
      <DialogTitle sx={{
        fontWeight: 800, bgcolor: "#0d1b2a", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.8, px: 2.5
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <ReceiptLongIcon />
          <Box>
            <Typography fontWeight={800} fontSize="1rem" lineHeight={1.2}>
              EV Profit &amp; Loss Detail
            </Typography>
            {/* API: data.invoiceNo ✅ */}
            <Typography fontSize="0.72rem" color="#90caf9">
              {d?.invoiceNo || ""}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ background: "#f4f6fb", p: 2.5 }}>
        {detailLoading ? (
          <Box textAlign="center" py={6}><CircularProgress /></Box>
        ) : d ? (
          <Grid container spacing={2}>

            {/* ─── 1. VEHICLE & SALE IDENTITY ─────────────────────────────── */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                <Typography fontWeight={800} mb={1.5} color="#1565c0"
                  display="flex" alignItems="center" gap={0.8} fontSize="0.85rem">
                  <TwoWheelerIcon fontSize="small" /> EV Sale &amp; Vehicle Info
                </Typography>
                <Grid container spacing={1.5}>
                  {/* API: data.invoiceNo ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Invoice No"   value={d.invoiceNo} color="#1565c0" />
                  </Grid>
                  {/* API: data.saleDate ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Sale Date"    value={fmtDate(d.saleDate)} />
                  </Grid>
                  {/* API: data.location ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Location"     value={d.location} />
                  </Grid>
                  {/* API: data.vehicleType ✅ (E_RICKSHAW | SCOOTY) */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Vehicle Type" value={d.vehicleType?.replace("_", " ")} />
                  </Grid>

                  {/* API: data.companyName ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Company / Brand" value={d.companyName} />
                  </Grid>
                  {/* API: data.model ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Model"           value={d.model} />
                  </Grid>
                  {/* API: data.engineNumber ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Engine No"       value={d.engineNumber} />
                  </Grid>
                  {/* API: data.chasisNumber ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Serial No"      value={d.chasisNumber} />
                  </Grid>
                  {/* API: data.noPlate ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Number Plate"    value={d.noPlate} />
                  </Grid>
                  {/* API: data.GSTIN ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="GSTIN"           value={d.GSTIN} />
                  </Grid>

                  {/* API: data.customerName ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Customer" value={d.customerName} />
                  </Grid>
                  {/* API: data.mobile ✅ (array in EV model) */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Mobile"
                      value={Array.isArray(d.mobile) ? d.mobile.join(", ") : (d.mobile || "—")} />
                  </Grid>
                  {/* API: data.fatherName ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Father Name"   value={d.fatherName} />
                  </Grid>
                  {/* API: data.village ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Village"        value={d.village} />
                  </Grid>
                  {/* API: data.postOffice ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Post Office"    value={d.postOffice} />
                  </Grid>
                  {/* API: data.policeStation ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Police Station" value={d.policeStation} />
                  </Grid>
                  {/* API: data.pinCode ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Pin Code"       value={d.pinCode} />
                  </Grid>
                  {/* API: data.address ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Address"        value={d.address} />
                  </Grid>
                  {/* API: data.saleType ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Sale Type"      value={d.saleType} />
                  </Grid>

                  {/*
                    ⚠️ REMOVED: landmark  — NOT in EV response (bike-only field)
                    ⚠️ REMOVED: remarks   — NOT projected in $project stage / not in API response
                    ⚠️ REMOVED: keyNumber — NOT in EV model at all
                  */}
                </Grid>
              </Paper>
            </Grid>

            {/* ─── 2. PAYMENT MODE ─────────────────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                <Typography fontWeight={800} mb={1.5} color="#0277bd" fontSize="0.85rem">
                  💳 Payment Mode
                </Typography>
                <Grid container spacing={1.5}>
                  {/* API: data.salePrice ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Sale Price (ExShowroom)"
                      value={`₹${fmt(d.salePrice)}`} color="#1565c0" />
                  </Grid>
                  {/* API: data.orp ✅ — present in EV model, used as revenue base in P&L */}
                  <Grid item xs={6}>
                    <InfoBlock label="ORP (On-Road Price)"
                      value={`₹${fmt(d.orp)}`} color="#1565c0" />
                  </Grid>
                 
                  {/* API: data.cashAmount ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Cash Amount"     value={`₹${fmt(d.cashAmount)}`} />
                  </Grid>
                  {/* API: data.finance.companyName ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Finance Company" value={d.finance?.companyName || "—"} />
                  </Grid>
                  {/* API: data.finance.amount ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Finance Amount"  value={`₹${fmt(d.finance?.amount)}`} />
                  </Grid>
                  {/*
                    ⚠️ REMOVED: finance.months — NOT in API $project / response object
                  */}
                  {/* API: data.discount ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Discount"
                      value={`₹${fmt(d.discount)}`}
                      color={d.discount > 0 ? "#c62828" : "#aaa"} />
                  </Grid>
                  {/* API: data.couponCashback ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Coupon Cashback"
                      value={`₹${fmt(d.couponCashback)}`}
                      color={d.couponCashback > 0 ? "#c62828" : "#aaa"} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ─── 3. P&L BREAKDOWN ────────────────────────────────────────── */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2, height: "100%",
                border: `1.5px solid ${d.status === "PROFIT" ? "#a5d6a7" : "#ef9a9a"}`,
                bgcolor: d.status === "PROFIT" ? "#f1f8e9" : "#fff8f8"
              }}>
                <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                  color={d.status === "PROFIT" ? "#2e7d32" : "#c62828"}
                  display="flex" alignItems="center" gap={0.8}>
                  {d.status === "PROFIT"
                    ? <TrendingUpIcon fontSize="small" />
                    : <TrendingDownIcon fontSize="small" />}
                  P&amp;L Breakdown
                </Typography>
                <Grid container spacing={1.5}>
                  {/* API: data.orp ✅ — revenue base for EV P&L formula */}
                  <Grid item xs={6}>
                    <InfoBlock label="ORP (Revenue Base)"
                      value={`₹${fmt(d.orp)}`} color="#1565c0" />
                  </Grid>
                  {/* API: data.purchasePrice ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Purchase Price"
                      value={`₹${fmt(d.purchasePrice)}`} color="#c62828" />
                  </Grid>
               
                  {/* API: data.totalPurchaseCost ✅ — purchasePrice + transportCost */}
                  {/* <Grid item xs={6}>
                    <InfoBlock label="Total Purchase Cost"
                      value={`₹${fmt(d.totalPurchaseCost)}`} color="#c62828" />
                  </Grid> */}
                  {/* API: data.expenses.total ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Expenses Total"
                      value={`₹${fmt(d.expenses?.total)}`} color="#e65100" />
                  </Grid>
                  {/* API: data.accessoryCost ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Accessory Cost"
                      value={`₹${fmt(d.accessoryCost)}`} color="#6a1b9a" />
                  </Grid>
                  {/* API: data.charges.rto ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="RTO"       value={`₹${fmt(d.charges?.rto)}`} />
                  </Grid>
                  {/* API: data.charges.insurance ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Insurance" value={`₹${fmt(d.charges?.insurance)}`} />
                  </Grid>
                  {/* API: data.discount ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Discount"
                      value={`₹${fmt(d.discount)}`} color="#c62828" />
                  </Grid> 
                    <Grid item xs={6}>
                    <InfoBlock label="Battery Sale Price"
                      value={`₹${fmt(d?.batterySalePrice)}`} color="#c62828" />
                  </Grid>
                     <Grid item xs={6}>
                    <InfoBlock label="Charger Sale Price"
                      value={`₹${fmt(d?.chargerSalePrice)}`} color="#c62828" />
                  </Grid>
                  {/* API: data.couponCashback ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Coupon Cashback"
                      value={`₹${fmt(d.couponCashback)}`} color="#c62828" />
                  </Grid>

                  {/*
                    ⚠️ REMOVED: salesmanIncentive — NOT in EV model / API response
                    ⚠️ REMOVED: exchange.exchangeProfit — NOT in API response
                    ⚠️ REMOVED: exchange.exchangeLossAmount — NOT in API response
                    These fields do NOT exist. exchangeBikeNetPL covers net exchange P/L.
                  */}

                  {/* API: d.hasDue ✅, d.due.remainingAmount ✅ */}
                  {d.hasDue && (
                    <Grid item xs={6}>
                      <InfoBlock label="Due Remaining (Deducted)"
                        value={`₹${fmt(d.due?.remainingAmount)}`} color="#f04013" />
                    </Grid>
                  )}

                  {}

                  {/* API: d.hasExchange ✅, d.exchange.remainingAmount ✅ */}
                  {d.hasExchange && (d.exchange?.remainingAmount ?? 0) > 0 && (
                    <Grid item xs={6}>
                      <InfoBlock label="Exch Remaining (Deducted)"
                        value={`₹${fmt(d.exchange?.remainingAmount)}`} color="#1565c0" />
                    </Grid>
                  )}

                  {/* API: d.exchange.exchangeBikeNetPL ✅ — net P/L from exchange vehicle */}
                  {d.hasExchange && (d.exchange?.exchangeBikeNetPL ?? 0) !== 0 && (
                    <Grid item xs={6}>
                      <InfoBlock
                        label="Exchange Net P/L"
                        value={`${d.exchange.exchangeBikeNetPL > 0 ? "+" : ""}₹${fmt(Math.abs(d.exchange.exchangeBikeNetPL))}`}
                        color={d.exchange.exchangeBikeNetPL >= 0 ? "#2e7d32" : "#c62828"}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                  {/* API: data.totalDeductions ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Total Deductions"
                      value={`₹${fmt(d.totalDeductions)}`} color="#c62828" />
                  </Grid>

                  {/* API: data.profitLoss ✅, data.status ✅ */}
                  <Grid item xs={6}>
                    <Label>Net P&amp;L</Label>
                    <Typography fontWeight={900} fontSize="1.05rem" mt={0.2}
                      color={d.status === "PROFIT" ? "#2e7d32" : "#c62828"}>
                      {d.status === "PROFIT" ? "+" : ""}₹{fmt(Math.abs(d.profitLoss))}
                    </Typography>
                    <Chip size="small" label={d.status}
                      icon={d.status === "PROFIT"
                        ? <TrendingUpIcon sx={{ fontSize: 11 }} />
                        : <TrendingDownIcon sx={{ fontSize: 11 }} />}
                      sx={{
                        mt: 0.5, fontWeight: 800, fontSize: "0.65rem",
                        bgcolor: d.status === "PROFIT" ? "#e8f5e9" : "#ffebee",
                        color:   d.status === "PROFIT" ? "#2e7d32" : "#c62828",
                        border:  `1px solid ${d.status === "PROFIT" ? "#a5d6a7" : "#ef9a9a"}`
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ─── 4. EXPENSES ─────────────────────────────────────────────── */}
            {/*
              EV expenses API response: { transport, tcr, regBroker, total }
              ⚠️ REMOVED: broker, amcRsa, petrol — bike-only, NOT in EV model/response
            */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, height: "100%", border: "1px solid #dce3f0" }}>
                <Typography fontWeight={800} mb={1.5} color="#00838f" fontSize="0.85rem">
                  💸 Expense Detail
                </Typography>
                <Grid container spacing={1.5}>
                  {[
                    /* API: data.expenses.transport ✅ */
                    ["Transport",  d?.expenses?.transport],
                    /* API: data.expenses.tcr ✅ */
                    ["TCR",        d?.expenses?.tcr],
                    /* API: data.expenses.regBroker ✅ */
                    ["Reg Broker", d?.expenses?.regBroker],
                  ].map(([lbl, val]) => (
                    <Grid item xs={6} key={lbl}>
                      <InfoBlock
                        label={lbl}
                        value={`₹${fmt(val ?? 0)}`}
                        color={(val ?? 0) > 0 ? "#e65100" : "#aaa"}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}><Divider sx={{ my: 0.3 }} /></Grid>
                  {/* API: data.expenses.total ✅ */}
                  <Grid item xs={6}>
                    <InfoBlock label="Total Expenses"
                      value={`₹${fmt(d?.expenses?.total)}`} color="#e65100" />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* ─── 5. ACCESSORY DETAIL (FIFO) ──────────────────────────────── */}
            {/* API: data.accessoryDetail ✅ — { helmet, seatCover, bikeCover, gift }
                Each: { qty, cost, breakdown: [{ qty, price, subtotal, fallback? }] } */}
            {d.accessoryDetail && (
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dce3f0" }}>
                  <Typography fontWeight={800} mb={1.5} color="#6a1b9a" fontSize="0.85rem">
                    🎁 Accessory Detail (FIFO Costing)
                  </Typography>
                  <Grid container spacing={1.5}>
                    {[
                      ["Helmet",     d.accessoryDetail.helmet],
                      ["Seat Cover", d.accessoryDetail.seatCover],
                      ["Bike Cover", d.accessoryDetail.bikeCover],
                      ["Gift",       d.accessoryDetail.gift],
                    ].map(([lbl, acc]) => (
                      <Grid item xs={12} sm={3} key={lbl}>
                        <Paper elevation={0} sx={{
                          p: 1.5, borderRadius: 1.5,
                          bgcolor: (acc?.qty ?? 0) > 0 ? "#f3e5f5" : "#fafafa",
                          border: `1px solid ${(acc?.qty ?? 0) > 0 ? "#ce93d8" : "#e0e0e0"}`
                        }}>
                          <Typography fontWeight={800} fontSize="0.75rem" color="#6a1b9a" mb={1}>
                            {lbl}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" mb={0.8}>
                            <Box>
                              <Label color="#888">Qty</Label>
                              <Val>{acc?.qty ?? 0}</Val>
                            </Box>
                            <Box textAlign="right">
                              <Label color="#888">Cost</Label>
                              <Val color="#6a1b9a">₹{fmt(acc?.cost ?? 0)}</Val>
                            </Box>
                          </Box>
                          {acc?.breakdown?.length > 0 ? (
                            <Box>
                              <Label color="#888">FIFO Batches</Label>
                              <Box mt={0.5} sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid #e1bee7" }}>
                                {acc.breakdown.map((bd, i) => (
                                  <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                                    sx={{
                                      px: 1, py: 0.5,
                                      bgcolor: i % 2 === 0 ? "#fff" : "#faf5ff",
                                      borderBottom: i < acc.breakdown.length - 1 ? "1px dashed #e1bee7" : "none"
                                    }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {bd.qty} × ₹{fmt(bd.price)}{bd.fallback ? " ⚠️" : ""}
                                    </Typography>
                                    <Typography variant="caption" fontWeight={700} color="#6a1b9a">
                                      ₹{fmt(bd.subtotal)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">No stock used</Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Box mt={1.5} display="flex" justifyContent="flex-end">
                    <Chip
                      label={`Total Accessory Cost: ₹${fmt(d.accessoryCost)}`}
                      size="small"
                      sx={{
                        bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 800,
                        border: "1px solid #ce93d8", fontSize: "0.72rem"
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}

            {/* ─── 6. EXCHANGE VEHICLE ─────────────────────────────────────── */}
            {/*
              API exchange object keys: ✅
                ownerName, location, model, registrationNumber, phone,
                exchangePrice, paidAmount, remainingAmount, status, payments,
                exchangeBikeStatus ("NONE"|"IN_STOCK"|"SOLD"),
                exchangeBikeSellPrice, exchangeCostUsed, exchangeBikeNetPL

              ⚠️ NOT in API: exchangeProfit, exchangeLossAmount (removed from modal)
            */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2,
                border: `1px solid ${d.hasExchange ? "#90caf9" : "#a5d6a7"}`,
                bgcolor: d.hasExchange ? "#e3f2fd" : "#f1f8e9"
              }}>
                <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                  color={d.hasExchange ? "#1565c0" : "#2e7d32"}
                  display="flex" alignItems="center" gap={0.8}>
                  <SwapHorizIcon fontSize="small" /> Exchange Vehicle
                </Typography>

                {/* API: d.hasExchange ✅ + d.exchange.exchangePrice ✅ */}
                {d.hasExchange && (d.exchange?.exchangePrice ?? 0) > 0 ? (
                  <Grid container spacing={1.5}>
                    {/* API: d.exchange.ownerName ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Owner Name"   value={d.exchange?.ownerName || "—"} />
                    </Grid>
                    {/* API: d.exchange.model ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Vehicle Model" value={d.exchange?.model || "—"} />
                    </Grid>
                    {/* API: d.exchange.registrationNumber ✅ — EV field (not regNo) */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Reg Number"   value={d.exchange?.registrationNumber || "—"} />
                    </Grid>
                    {/* API: d.exchange.phone ✅ — EV field (not mobile) */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Phone"         value={d.exchange?.phone || "—"} />
                    </Grid>
                    {/* API: d.exchange.location ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Exchange Location" value={d.exchange?.location || "—"} />
                    </Grid>
                    {/* API: d.exchange.exchangePrice ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Exchange Price"
                        value={`₹${fmt(d.exchange?.exchangePrice)}`} color="#1565c0" />
                    </Grid>
                    {/* API: d.exchange.paidAmount ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Paid to Customer"
                        value={`₹${fmt(d.exchange?.paidAmount)}`} color="#2e7d32" />
                    </Grid>
                    {/* API: d.exchange.remainingAmount ✅ */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Remaining"
                        value={`₹${fmt(d.exchange?.remainingAmount)}`}
                        color={(d.exchange?.remainingAmount ?? 0) > 0 ? "#1565c0" : "#2e7d32"} />
                    </Grid>
                    {/* API: d.exchange.exchangeCostUsed ✅ — actual cost used in deductions */}
                    <Grid item xs={6} sm={3}>
                      <InfoBlock label="Cost Used in P&L"
                        value={`₹${fmt(d.exchange?.exchangeCostUsed)}`} color="#e65100" />
                    </Grid>

                    {/* API: d.exchange.exchangeBikeStatus ✅ — NONE | IN_STOCK | SOLD */}
                    <Grid item xs={6} sm={3}>
                      <Label>Stock Status</Label>
                      <Box mt={0.3}>
                        <Chip
                          label={d.exchange?.exchangeBikeStatus || "NONE"}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: "0.7rem",
                            bgcolor:
                              d.exchange?.exchangeBikeStatus === "SOLD"     ? "#e8f5e9" :
                              d.exchange?.exchangeBikeStatus === "IN_STOCK" ? "#fff3e0" : "#f5f5f5",
                            color:
                              d.exchange?.exchangeBikeStatus === "SOLD"     ? "#2e7d32" :
                              d.exchange?.exchangeBikeStatus === "IN_STOCK" ? "#e65100" : "#888",
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* API: d.exchange.exchangeBikeSellPrice ✅ — only meaningful when SOLD */}
                    {d.exchange?.exchangeBikeStatus === "SOLD" && (
                      <Grid item xs={6} sm={3}>
                        <InfoBlock label="Resale Price"
                          value={`₹${fmt(d.exchange?.exchangeBikeSellPrice)}`} color="#2e7d32" />
                      </Grid>
                    )}

                    {/* API: d.exchange.exchangeBikeNetPL ✅ */}
                    {(d.exchange?.exchangeBikeNetPL ?? 0) !== 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper elevation={0} sx={{
                          p: 1, borderRadius: 1.5,
                          bgcolor: (d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "#e8f5e9" : "#ffebee",
                          border:  `1px solid ${(d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "#a5d6a7" : "#ef9a9a"}`
                        }}>
                          <Label color={(d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "#2e7d32" : "#c62828"}>
                            Exchange Net P/L
                          </Label>
                          <Typography fontWeight={800} fontSize="0.9rem" mt={0.3}
                            color={(d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "#2e7d32" : "#c62828"}>
                            {(d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "+" : "-"}
                            ₹{fmt(Math.abs(d.exchange?.exchangeBikeNetPL ?? 0))}
                          </Typography>
                          <Typography variant="caption" color="#555">
                            {(d.exchange?.exchangeBikeNetPL ?? 0) >= 0 ? "Resale ≥ agreed price" : "Not yet sold / loss"}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {/* API: d.exchange.status ✅ — PAID | DUE */}
                    <Grid item xs={6} sm={3}>
                      <Label>Payment Status</Label>
                      <Box mt={0.3}>
                        <Chip
                          label={d.exchange?.status || "PAID"}
                          size="small"
                          icon={(d.exchange?.remainingAmount ?? 0) > 0
                            ? <WarningAmberIcon sx={{ fontSize: 13 }} />
                            : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                          sx={{
                            fontWeight: 700, fontSize: "0.72rem",
                            bgcolor: (d.exchange?.remainingAmount ?? 0) > 0 ? "#e3f2fd" : "#e8f5e9",
                            color:   (d.exchange?.remainingAmount ?? 0) > 0 ? "#1565c0" : "#2e7d32",
                            border: `1px solid ${(d.exchange?.remainingAmount ?? 0) > 0 ? "#90caf9" : "#a5d6a7"}`
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}><Divider sx={{ my: 0.3 }} /></Grid>

                    {/*
                      EV exchange payment schema (from model):
                        amount ✅, date ✅, name ✅, phone ✅
                      ⚠️ No 'note' or 'remark' in exchange payments (those are due payment fields)
                    */}
                    {d.exchange?.payments?.length > 0 && (
                      <Grid item xs={12}>
                        <Label>Payment History ({d.exchange.payments.length} payments)</Label>
                        <Box mt={0.8} sx={{ maxHeight: 140, overflowY: "auto", borderRadius: 1.5, border: "1px solid #90caf9" }}>
                          {d.exchange.payments.map((p, i) => (
                            <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                              sx={{
                                px: 1.5, py: 0.8,
                                bgcolor: i % 2 === 0 ? "#fff" : "#f0f7ff",
                                borderBottom: i < d.exchange.payments.length - 1 ? "1px dashed #90caf9" : "none"
                              }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box sx={{
                                  width: 20, height: 20, borderRadius: "50%", bgcolor: "#1565c0",
                                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                }}>
                                  <Typography variant="caption" color="#fff" fontWeight={800} fontSize="0.6rem">
                                    {i + 1}
                                  </Typography>
                                </Box>
                                <Box>
                                  {/* API: exchange.payments[].name ✅ */}
                                  <Typography variant="caption" fontWeight={700} display="block">
                                    {p.name || "—"}
                                  </Typography>
                                  {/* API: exchange.payments[].phone ✅ */}
                                  <Typography variant="caption" color="text.secondary">
                                    {p.phone+"   " || ""}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box textAlign="right">
                                {/* API: exchange.payments[].amount ✅ */}
                                <Typography variant="caption" fontWeight={800} color="#1565c0" display="block">
                                  ₹{fmt(p.amount)}
                                </Typography>
                                {/* API: exchange.payments[].date ✅ */}
                                <Typography variant="caption" color="text.secondary">
                                  {fmtDate(p.date)}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                    No exchange vehicle on this sale.
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* ─── 7. DUE / PAYMENT STATUS ─────────────────────────────────── */}
            {/*
              API due object: { totalAmount, paidAmount, remainingAmount, status, payments }
              API due payments: [{ amount, date, note, remark }] ✅ — both fields exist in EV model
            */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: 2,
                border: `1px solid ${d.hasDue ? "#ffb74d" : "#a5d6a7"}`,
                bgcolor: d.hasDue ? "#fff8f5" : "#f1f8e9"
              }}>
                <Typography fontWeight={800} mb={1.5} fontSize="0.85rem"
                  color={d.hasDue ? "#e65100" : "#2e7d32"}
                  display="flex" alignItems="center" gap={0.8}>
                  <PaymentIcon fontSize="small" /> Customer Due / Payment Status
                </Typography>
                <Grid container spacing={1.5}>
                  {/* API: d.due.status ✅ */}
                  <Grid item xs={6} sm={3}>
                    <Label>Status</Label>
                    <Box mt={0.3}>
                      <Chip
                        label={d.due?.status || "PAID"}
                        size="small"
                        icon={d.hasDue
                          ? <WarningAmberIcon sx={{ fontSize: 13 }} />
                          : <CheckCircleIcon sx={{ fontSize: 13 }} />}
                        sx={{
                          fontWeight: 700, fontSize: "0.72rem",
                          bgcolor: d.hasDue ? "#fff3e0" : "#e8f5e9",
                          color:   d.hasDue ? "#e65100" : "#2e7d32",
                          border: `1px solid ${d.hasDue ? "#ffb74d" : "#a5d6a7"}`
                        }}
                      />
                    </Box>
                  </Grid>
                  {/* API: d.due.totalAmount ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Total Due Amount"
                      value={`₹${fmt(d.due?.totalAmount)}`} />
                  </Grid>
                  {/* API: d.due.paidAmount ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Paid So Far"
                      value={`₹${fmt(d.due?.paidAmount)}`} color="#2e7d32" />
                  </Grid>
                  {/* API: d.due.remainingAmount ✅ */}
                  <Grid item xs={6} sm={3}>
                    <InfoBlock label="Remaining"
                      value={`₹${fmt(d.due?.remainingAmount)}`}
                      color={d.hasDue ? "#e65100" : "#2e7d32"} />
                  </Grid>
                </Grid>

                {/* Due installment history */}
                {d.due?.payments?.length > 0 && (
                  <Box mt={2}>
                    <Label>Installment History ({d.due.payments.length} payments)</Label>
                    <Box mt={0.8} sx={{ maxHeight: 160, overflowY: "auto", borderRadius: 1.5, border: "1px solid #ffe0b2" }}>
                      {d.due.payments.map((p, i) => (
                        <Box key={i} display="flex" justifyContent="space-between" alignItems="center"
                          sx={{
                            px: 1.5, py: 0.8,
                            bgcolor: i % 2 === 0 ? "#fff" : "#fff8f5",
                            borderBottom: i < d.due.payments.length - 1 ? "1px dashed #ffe0b2" : "none"
                          }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{
                              width: 20, height: 20, borderRadius: "50%", bgcolor: "#e65100",
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                            }}>
                              <Typography variant="caption" color="#fff" fontWeight={800} fontSize="0.6rem">
                                {i + 1}
                              </Typography>
                            </Box>
                            <Box>
                              {/* API: due.payments[].note ✅ — in EV model */}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {p.note || `Payment ${i + 1}`}
                              </Typography>
                              {/* API: due.payments[].remark ✅ — in EV model */}
                              {p.remark && (
                                <Typography variant="caption" color="text.disabled">
                                  {p.remark}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box textAlign="right">
                            {/* API: due.payments[].amount ✅ */}
                            <Typography variant="caption" fontWeight={800} color="#2e7d32" display="block">
                              ₹{fmt(p.amount)}
                            </Typography>
                            {/* API: due.payments[].date ✅ */}
                            <Typography variant="caption" color="text.secondary">
                              {fmtDate(p.date)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

          </Grid>
        ) : (
          <Alert severity="info">No data found</Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EvProfitLossDetailModal;